import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button as JigsawButton, Text as JigsawText } from '@solvhealth/jigsaw-v1';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Warning from '@material-ui/icons/Warning';
import ArrowForward from '@material-ui/icons/ArrowRightAlt';
import { fontFamily, fontSize } from '../../../../../constants/text';
import { colors } from '../../../../../constants/colors';
import { FadeIn } from '../../../../../core/util/commonCssAnimations';
import { getRelativeDayName, isDateToday } from '../../../../../core/util/date';
import { getBookAheadVisitsFullyBookedXDayCopy, getNoAvailabilityXDay } from '../util';
import {
  BOOK_MODULE_NO_MORE_ONLINE_VISITS_ALTERNATIVES_COPY,
  shouldShowFeature,
} from '../../../../../core/util/customization';

import { bookingTimeSelected, setIsAsapBooking } from '~/actions/newBooking';
import Button, { ButtonVariations } from '../../../../SolvPatternLibrary/Button';
import { analyticsTrackEvent } from '~/core/analytics';
import { ONLINE_WAITLIST_CLICK } from '~/core/analytics/events';
import { hasOnlineWaitlistAvailability } from '~/core/util/location';
import { useTrackShowOnlineWaitlist } from '~/components/BookingTimeSelector/Views/components/SlotSelectorStates/hooks';
import history from '~/core/history';
import { getBookingFlowEntryRoute } from '~/routes/book/components/BookingFlow/util';
import { BOOKING_FORM_PROFILE_ORIGIN_SRP } from '~/core/util/bookingFlowRouting';
import { InlineIndicator } from '~/components/InlineIndicator/InlineIndicator';
import { pluralize } from '~/core/util/string';
import useWaitListCount from '~/routes/book/hooks/useWaitListCount';

const TitleContainer = styled.div.withConfig({ componentId: 'sc-evyaj3-0' })`
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
`;

const StyledWarning = styled(Warning).withConfig({ componentId: 'sc-evyaj3-1' })`
  color: ${colors.peaches};
  font-size: 17px;
  margin-right: 6px;
`;

const Title = styled.div.withConfig({ componentId: 'sc-evyaj3-2' })`
  color: ${colors.peaches};
  font-family: ${fontFamily.bold};
  font-size: ${fontSize.mediumSmall};
  letter-spacing: -0.27px;
  line-height: 18px;
`;

const BookingWidgetTitle = styled(Title).withConfig({ componentId: 'sc-evyaj3-3' })`
  color: ${colors.myRightFoot};
`;

const Text = styled.div.withConfig({ componentId: 'sc-evyaj3-4' })`
  color: ${colors.blueMonday};
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  letter-spacing: -0.25px;
  line-height: 20px;
  text-align: center;
`;

const Container = styled.div.withConfig({ componentId: 'sc-evyaj3-5' })`
  animation: ${FadeIn} 300ms linear;
`;

const ButtonContainer = styled.div.withConfig({ componentId: 'sc-evyaj3-6' })`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
  margin-top: 20px;
`;

const NextAvailableButton = styled(Button).withConfig({ componentId: 'sc-evyaj3-7' })`
  padding: 0;
  height: 45px;
`;

const Copy = styled.span.withConfig({ componentId: 'sc-evyaj3-8' })`
  width: 70%;
`;

type Props = {
  selectedDate?: any;
  location?: any;
  shouldShowNextAvailableAppointmentCta: boolean;
  setAndHandleDate?: (...args: any[]) => any;
  handleClickShowMore?: (...args: any[]) => any;
  handleSelectSlot: (...args: any[]) => any;
  isBookingWidget?: boolean;
};

const NoSlotsLeft = (props: Props) => {
  const {
    selectedDate,
    location,
    shouldShowNextAvailableAppointmentCta,
    setAndHandleDate,
    handleClickShowMore,
    handleSelectSlot,
    isBookingWidget,
  } = props;

  const { t } = useTranslation();

  const {
    waitListCount,
    isValidWaitListCount,
    isLoading: isLoadingWaitList,
  } = useWaitListCount(location);

  const dispatch = useDispatch();

  const showOnlineWaitlist =
    hasOnlineWaitlistAvailability(location) && isDateToday(selectedDate, location.timezone);

  const showAlternativeOptionsCopy = shouldShowFeature(
    BOOK_MODULE_NO_MORE_ONLINE_VISITS_ALTERNATIVES_COPY,
    location
  );

  const handleFindNextAvailableVisit = () => {
    setAndHandleDate?.({ isMoreDate: true });
    handleClickShowMore?.();
  };

  const day = useMemo(
    () => getRelativeDayName(selectedDate, location.timeZone),
    [selectedDate, location.timeZone]
  );

  const getNextButtonVariation = () =>
    isBookingWidget ? ButtonVariations.pinkPrimary : ButtonVariations.pinkTertiary;

  const renderOtherOptions = () => {
    if (showAlternativeOptionsCopy) {
      return (
        <>
          <br />
          {t('bookingTimeSelector.slotSelectorStates.noSlotsLeft.youCanStillWalkIn')}
        </>
      );
    }

    return null;
  };

  useTrackShowOnlineWaitlist(showOnlineWaitlist);

  return (
    <Container>
      {!showOnlineWaitlist ? (
        <>
          <TitleContainer>
            <StyledWarning />
            {isBookingWidget ? (
              <BookingWidgetTitle>{getNoAvailabilityXDay(day, t)}</BookingWidgetTitle>
            ) : (
              <Title>
                {t('bookingTimeSelector.slotSelectorStates.noSlotsLeft.noVisitTimesLeft')}
              </Title>
            )}
          </TitleContainer>

          <Text>
            {isBookingWidget
              ? t('bookingTimeSelector.slotSelectorStates.noSlotsLeft.allOnlineSlotsFull')
              : getBookAheadVisitsFullyBookedXDayCopy(day, t)}
            {renderOtherOptions()}
          </Text>
          {shouldShowNextAvailableAppointmentCta && (
            <ButtonContainer onClick={handleFindNextAvailableVisit}>
              <NextAvailableButton variation={getNextButtonVariation()} width="90%">
                <Copy>
                  {t('bookingTimeSelector.slotSelectorStates.clinicClosed.findNextAvailableVisit')}
                </Copy>
                <ArrowForward />
              </NextAvailableButton>
            </ButtonContainer>
          )}
        </>
      ) : (
        <>
          <Box bg="gray-100" borderRadius="sm" px={1.5} py={1.5}>
            <JigsawText textAlign="center" textColor="gray-600" variant="body2">
              {t('bookingTimeSelector.slotSelectorStates.noSlotsLeft.stillAcceptingWalkIns')}
              {!isLoadingWaitList && isValidWaitListCount && (
                <InlineIndicator>
                  <JigsawText
                    color={colors.gray600}
                    fontWeight="medium"
                    textAlign="center"
                    variant="subtitle2"
                  >
                    {waitListCount} {pluralize(waitListCount, 'patient')} in line now
                  </JigsawText>
                </InlineIndicator>
              )}
            </JigsawText>
          </Box>
          <JigsawButton
            fullWidth
            mt={2}
            onClick={() => {
              analyticsTrackEvent(ONLINE_WAITLIST_CLICK, {
                location_id: location.id,
              });
              dispatch(setIsAsapBooking(true));
              dispatch(bookingTimeSelected(new Date().valueOf(), location.timeZone));
              history.push(getBookingFlowEntryRoute(true, BOOKING_FORM_PROFILE_ORIGIN_SRP, false));
            }}
            type="button"
            variant="outlined"
          >
            Get in line
          </JigsawButton>
        </>
      )}
    </Container>
  );
};

export default NoSlotsLeft;
